import { useEffect, useRef, useState } from 'react';

import { Code, Layers, Maximize2, Plus } from 'lucide-react';

import {
  convertFieldsToSchema,
  convertSchemaToFields,
} from '@/components/review-configs/review-field-schema';
import { ReviewFieldsSchemaEditor } from '@/components/review-configs/ReviewFieldsSchemaEditor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import type { ReviewFieldDefinition } from '@/components/review-configs/ReviewFieldEditor';
import type { AgentNodeData } from '@repo/shared-types';

interface Props {
  data: AgentNodeData;
  onChange: (data: AgentNodeData) => void;
}

/** Canonical run names the calls_with_review view promotes onto dashboards. */
const CANONICAL_NAMES = ['classification', 'quality'];

const EMPTY_SCHEMA = { type: 'object' as const, properties: {}, required: [] };

export const AgentConfig = ({ data, onChange }: Props) => {
  const update = (patch: Partial<AgentNodeData>) => onChange({ ...data, ...patch });

  /**
   * The field list is local state, mirroring ReviewConfigForm.
   *
   * It cannot be derived from data.outputSchema on every render:
   * convertFieldsToSchema drops any field without a name, so a freshly added
   * (still unnamed) row would round-trip straight back out of existence and
   * "Add field" would look like it did nothing. The JSON schema is the saved
   * artifact; this array is what you are editing.
   *
   * NodeConfigPanel keys this component by node id, so selecting another node
   * remounts it and re-seeds from that node's schema.
   */
  const [fields, setFields] = useState<ReviewFieldDefinition[]>(() =>
    convertSchemaToFields(data.outputSchema),
  );
  const [requiredFields, setRequiredFields] = useState<string[]>(
    () => data.outputSchema?.required ?? [],
  );
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [schemaMode, setSchemaMode] = useState<'visual' | 'json'>('visual');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');

  // The editor's updateField calls onChange AND onRequiredFieldsChange in the
  // same handler. Anything that needs both values at once would therefore read
  // one of them from a stale closure and clobber the other — typing a field
  // name would immediately revert. So the two setters stay independent (as in
  // ReviewConfigForm) and the schema is pushed up here, after React has
  // batched both updates.
  const latest = useRef({ data, onChange });
  const mounted = useRef(false);

  // Declared before the push effect below, so the ref is fresh by the time it
  // runs. (Writing it during render is a React violation.)
  useEffect(() => {
    latest.current = { data, onChange };
  });

  useEffect(() => {
    // Don't fire on mount: that would dirty the node just by selecting it.
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const { data: current, onChange: emit } = latest.current;
    emit({
      ...current,
      outputSchema: convertFieldsToSchema(fields, requiredFields) ?? EMPTY_SCHEMA,
    });
  }, [fields, requiredFields]);

  const namedFields = fields.filter((f) => f.name);
  const persistsToDashboard = data.persistTo !== 'none' && CANONICAL_NAMES.includes(data.outputKey);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={data.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Agent"
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Prompt</label>
        <textarea
          className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-16 w-full rounded-md border px-3 py-2 text-xs focus-visible:ring-2 focus-visible:outline-none"
          value={data.prompt}
          onChange={(e) => update({ prompt: e.target.value })}
          placeholder="Describe what this agent should do and how to fill the output fields…"
          rows={8}
        />
        <p className="text-muted-foreground text-[10px]">
          Supports {`{{variables}}`} from earlier nodes, e.g. {`{{classification.labels.x}}`}. Use{' '}
          <code className="font-mono">{`{{transcript}}`}</code> to drop the call transcript straight
          into the prompt — then you can leave &ldquo;Include the call transcript&rdquo; off to
          avoid sending it twice.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Output Key</label>
        <Input
          value={data.outputKey}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="agent"
          className="font-mono text-xs"
        />
        <p className="text-muted-foreground text-[10px]">
          Later nodes read {`{{<outputKey>.<field>}}`} using the field names below.
        </p>
      </div>

      <div className="space-y-2 border-t pt-3">
        <label className="text-muted-foreground text-xs font-medium">Input</label>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={data.includeTranscript}
            onChange={(e) => update({ includeTranscript: e.target.checked })}
          />
          Include the call transcript
        </label>
        {data.includeTranscript && (
          <label className="ml-5 flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={data.includeOriginalTranscript}
              onChange={(e) => update({ includeOriginalTranscript: e.target.checked })}
            />
            Use the original transcript (not the enhanced one)
          </label>
        )}
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={data.includeAudio}
            onChange={(e) => update({ includeAudio: e.target.checked })}
          />
          Include the call audio
        </label>
        {data.includeAudio && (
          <p className="text-muted-foreground ml-5 text-[10px]">
            The agent transcribes the audio in the same request and{' '}
            <strong>overwrites the call&apos;s enhanced transcript</strong> — the same row the
            Review node writes, which the call detail page reads. Calls without a recording fall
            back to text only.
          </p>
        )}
      </div>

      <div className="space-y-2 border-t pt-3">
        <div className="flex items-center justify-between">
          <label className="text-muted-foreground text-xs font-medium">Output Schema</label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => setSchemaOpen(true)}
          >
            {namedFields.length === 0 ? (
              <>
                <Plus size={12} className="mr-1" /> Add fields
              </>
            ) : (
              <>
                <Maximize2 size={12} className="mr-1" /> Edit
              </>
            )}
          </Button>
        </div>
        <p className="text-muted-foreground text-[10px]">
          The agent returns exactly these fields as JSON.
        </p>
        {namedFields.length === 0 ? (
          <button
            type="button"
            onClick={() => setSchemaOpen(true)}
            className="text-muted-foreground hover:border-foreground/40 hover:text-foreground w-full rounded-md border border-dashed px-3 py-4 text-xs transition-colors"
          >
            No fields yet — click to define the output
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setSchemaOpen(true)}
            className="hover:bg-muted/50 w-full rounded-md border p-2 text-left transition-colors"
          >
            <div className="flex flex-wrap gap-1">
              {namedFields.slice(0, 8).map((f) => (
                <Badge key={f.name} variant="secondary" className="font-mono text-[10px]">
                  {f.name}
                  <span className="text-muted-foreground ml-1">{f.type}</span>
                </Badge>
              ))}
              {namedFields.length > 8 && (
                <Badge variant="outline" className="text-[10px]">
                  +{namedFields.length - 8} more
                </Badge>
              )}
            </div>
          </button>
        )}
      </div>

      <Dialog open={schemaOpen} onOpenChange={setSchemaOpen}>
        <DialogContent className="flex h-[90vh] max-w-[95vw] flex-col gap-0 p-0 sm:max-w-[95vw]">
          <DialogHeader className="border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-base">Output schema — {data.label}</DialogTitle>
                <DialogDescription className="text-xs">
                  The agent returns exactly these fields as JSON. Later nodes read them as{' '}
                  <code className="font-mono">{`{{${data.outputKey || 'agent'}.<field>}}`}</code>.
                  Boolean fields become labels/flags when saving; everything else becomes data.
                </DialogDescription>
              </div>
              <div className="bg-muted inline-flex rounded-lg p-1">
                <Button
                  type="button"
                  onClick={() => {
                    if (schemaMode === 'json') {
                      try {
                        const parsed = JSON.parse(jsonText);
                        if (!parsed || typeof parsed !== 'object') {
                          setJsonError('Must be a JSON object');
                          return;
                        }
                        const converted = convertSchemaToFields(parsed);
                        setFields(converted);
                        setRequiredFields(parsed.required ?? []);
                        setJsonError('');
                      } catch {
                        setJsonError('Invalid JSON — fix errors before switching to visual mode');
                        return;
                      }
                    }
                    setSchemaMode('visual');
                  }}
                  size="sm"
                  variant={schemaMode === 'visual' ? 'default' : 'ghost'}
                  className="h-7 px-3 text-xs"
                >
                  <Layers size={14} className="mr-1" />
                  Visual
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    const schema = convertFieldsToSchema(fields, requiredFields) ?? EMPTY_SCHEMA;
                    setJsonText(JSON.stringify(schema, null, 2));
                    setJsonError('');
                    setSchemaMode('json');
                  }}
                  size="sm"
                  variant={schemaMode === 'json' ? 'default' : 'ghost'}
                  className="h-7 px-3 text-xs"
                >
                  <Code size={14} className="mr-1" />
                  JSON
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {schemaMode === 'json' ? (
              <div className="space-y-2">
                <p className="text-muted-foreground text-xs">
                  Edit the output schema as raw JSON. Must be a valid JSON Schema object with{' '}
                  <code className="font-mono">type</code>,{' '}
                  <code className="font-mono">properties</code>, and optionally{' '}
                  <code className="font-mono">required</code>.
                </p>
                <textarea
                  value={jsonText}
                  onChange={(e) => {
                    setJsonText(e.target.value);
                    try {
                      const parsed = JSON.parse(e.target.value);
                      if (!parsed || typeof parsed !== 'object') {
                        setJsonError('Must be a JSON object');
                      } else {
                        setJsonError('');
                      }
                    } catch {
                      setJsonError('Invalid JSON');
                    }
                  }}
                  rows={24}
                  className={`border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring w-full rounded-md border px-3 py-2 font-mono text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
                    jsonError ? 'border-destructive' : ''
                  }`}
                  spellCheck={false}
                  aria-label="Output schema JSON"
                  aria-invalid={!!jsonError}
                  aria-describedby={jsonError ? 'schema-json-error' : undefined}
                />
                {jsonError && (
                  <p id="schema-json-error" className="text-destructive text-sm" role="alert">
                    {jsonError}
                  </p>
                )}
              </div>
            ) : (
              <ReviewFieldsSchemaEditor
                value={fields}
                onChange={setFields}
                requiredFields={requiredFields}
                onRequiredFieldsChange={setRequiredFields}
              />
            )}
          </div>
          <div className="flex justify-end border-t px-6 py-3">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (schemaMode === 'json' && !jsonError && jsonText.trim()) {
                  try {
                    const parsed = JSON.parse(jsonText);
                    const converted = convertSchemaToFields(parsed);
                    setFields(converted);
                    setRequiredFields(parsed.required ?? []);
                  } catch {
                    // Ignore — will stay on JSON errors
                  }
                }
                setSchemaMode('visual');
                setSchemaOpen(false);
              }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-2 border-t pt-3">
        <label className="text-muted-foreground text-xs font-medium">Save Results</label>
        <select
          value={data.persistTo}
          onChange={(e) => update({ persistTo: e.target.value as AgentNodeData['persistTo'] })}
          className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <option value="none">Don&apos;t save (workflow only)</option>
          <option value="classification">Save as a classification</option>
          <option value="quality">Save as a quality assessment</option>
          <option value="review">Save as a review</option>
        </select>
        <p className="text-muted-foreground text-[10px]">
          {data.persistTo === 'review'
            ? 'Creates a call_reviews row with the full output in additional_review_data. This will become the latest review for the call on dashboards.'
            : 'Saved rows land in the same table the Classifier / Quality nodes write to, under the output key as their name. Boolean fields become labels/flags; everything else becomes data.'}
        </p>
        {persistsToDashboard && data.persistTo !== 'review' && (
          <p className="text-[10px] font-medium text-amber-600">
            &ldquo;{data.outputKey}&rdquo; is the canonical name that dashboards read. This node
            will replace the {data.outputKey} row written by the{' '}
            {data.persistTo === 'quality' ? 'Quality' : 'Classifier'} node. Use a different output
            key to run alongside it instead.
          </p>
        )}
        {data.persistTo === 'review' && (
          <p className="text-[10px] font-medium text-amber-600">
            The calls_with_review view promotes the newest completed review per call. This
            agent-written row will displace any earlier Review node row for the same call.
          </p>
        )}
        {data.persistTo !== 'none' && (
          <div className="space-y-1 pt-1">
            <label className="text-muted-foreground text-xs font-medium">Version</label>
            <Input
              value={data.version}
              onChange={(e) => update({ version: e.target.value })}
              placeholder="agent-v1"
              className="font-mono text-xs"
            />
            <p className="text-muted-foreground text-[10px]">
              Stored alongside each row so you can tell which prompt revision produced it.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2 border-t pt-3">
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">Model (optional)</label>
          <Input
            value={data.model ?? ''}
            onChange={(e) => update({ model: e.target.value || undefined })}
            placeholder="gemini-2.5-flash"
            className="font-mono text-xs"
          />
        </div>
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">
            Temperature (optional)
          </label>
          <Input
            type="number"
            min={0}
            max={2}
            step={0.1}
            value={data.temperature ?? ''}
            onChange={(e) =>
              update({ temperature: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            placeholder="0.2"
            className="font-mono text-xs"
          />
        </div>
      </div>
    </div>
  );
};
